import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, X, Upload, Save, Trash2, Edit2, Check, GripVertical, Plus } from 'lucide-react';
import { useData } from '../../context/DataContext';
import { Product, Material, FinishingOption, ColorSpec, FixedSize, CustomOption, CustomOptionChoice, Category } from '../../types';
import { compressImage, getBase64Size, formatBytes } from '../../utils/imageCompression';

const AdminProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const copyFromId = searchParams.get('copy');
  const { products, categories, addProduct, updateProduct, deleteProduct, loading, error, loadAllProducts } = useData();
  const [product, setProduct] = useState<Product | null>(null);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [finishingOptions, setFinishingOptions] = useState<FinishingOption[]>([]);
  const [colorSpecs, setColorSpecs] = useState<ColorSpec[]>([]);
  const [fixedSizes, setFixedSizes] = useState<FixedSize[]>([]);
  const [customOptions, setCustomOptions] = useState<CustomOption[]>([]);
  const [newMaterial, setNewMaterial] = useState<Material>({ id: '', name: '', pricingType: 'multiplier', priceMultiplier: 1 });
  const [newFinishing, setNewFinishing] = useState<FinishingOption>({ id: '', name: '', pricingType: 'flat', price: 0 });
  const [newColorSpec, setNewColorSpec] = useState<ColorSpec>({ id: '', name: '', price: 0 });
  const [newFixedSize, setNewFixedSize] = useState<FixedSize>({ id: '', width: 12, height: 12, price: 0 });
  const [newCustomOption, setNewCustomOption] = useState<CustomOption>({ id: '', name: '', required: false, options: [] });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [productImages, setProductImages] = useState<string[]>([]);
  const [editingMaterialId, setEditingMaterialId] = useState<string | null>(null);
  const [editingFinishingId, setEditingFinishingId] = useState<string | null>(null);
  const [editingColorSpecId, setEditingColorSpecId] = useState<string | null>(null);
  const [editingFixedSizeId, setEditingFixedSizeId] = useState<string | null>(null);
  const [editingCustomOptionId, setEditingCustomOptionId] = useState<string | null>(null);
  const [isCustomizable, setIsCustomizable] = useState<boolean>(true);
  const [draggedFinishingIndex, setDraggedFinishingIndex] = useState<number | null>(null);

  const isNewProduct = id === 'new';

  // Load all products on mount to ensure we have the full list
  useEffect(() => {
    loadAllProducts().catch(console.error);
  }, []);

  useEffect(() => {
    if (!isNewProduct && id) {
      const existingProduct = products.find(p => p.id === id);
      if (existingProduct) {
        setProduct(existingProduct);
        setMaterials(existingProduct.materials || []);
        setFinishingOptions(existingProduct.finishingOptions || []);
        setColorSpecs(existingProduct.colorSpecs || []);
        setFixedSizes(existingProduct.fixedSizes || []);
        setCustomOptions(existingProduct.customOptions || []);
        setIsCustomizable(existingProduct.customizable);
        setImagePreview(existingProduct.image || '');
        // Initialize images array - combine primary image with additional images
        const allImages = [];
        if (existingProduct.image) {
          allImages.push(existingProduct.image);
        }
        if (existingProduct.images && existingProduct.images.length > 0) {
          allImages.push(...existingProduct.images);
        }
        setProductImages(allImages);
      }
    } else if (isNewProduct) {
      // Calculate displayOrder for new product
      const maxDisplayOrder = products.reduce((max, p) => {
        return Math.max(max, p.displayOrder || 0);
      }, 0);
      
      // Check if we're copying from another product
      if (copyFromId) {
        const productToCopy = products.find(p => p.id === copyFromId);
        if (productToCopy) {
          // Create a copy of the product with a new ID and name
          setProduct({
            ...productToCopy,
            id: `product-${Date.now()}`,
            name: `${productToCopy.name} (Copy)`,
            displayOrder: maxDisplayOrder + 1,
            active: true,
          });
          setMaterials([...productToCopy.materials || []]);
          setFinishingOptions([...productToCopy.finishingOptions || []]);
          setColorSpecs([...productToCopy.colorSpecs || []]);
          setFixedSizes([...productToCopy.fixedSizes || []]);
          setCustomOptions([...productToCopy.customOptions || []]);
          setIsCustomizable(productToCopy.customizable);
          setImagePreview(productToCopy.image || '');
          // Copy images - combine primary image with additional images
          const allImages = [];
          if (productToCopy.image) {
            allImages.push(productToCopy.image);
          }
          if (productToCopy.images && productToCopy.images.length > 0) {
            allImages.push(...productToCopy.images);
          }
          setProductImages(allImages);
        } else {
          // Fallback to creating a new product if copy source not found
          setProduct({
            id: `product-${Date.now()}`,
            name: '',
            category: '',
            basePrice: 0,
            description: '',
            image: '',
            customizable: true,
            materials: [],
            finishingOptions: [],
            colorSpecs: [],
            fixedSizes: [],
            customOptions: [],
            displayOrder: maxDisplayOrder + 1,
            active: true,
          });
        }
      } else {
        // Create a new product from scratch
        setProduct({
          id: `product-${Date.now()}`,
          name: '',
          category: '',
          basePrice: 0,
          description: '',
          image: '',
          customizable: true,
          materials: [],
          finishingOptions: [],
          colorSpecs: [],
          fixedSizes: [],
          customOptions: [],
          displayOrder: maxDisplayOrder + 1,
          active: true,
        });
      }
    }
  }, [id, products, isNewProduct, copyFromId]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    // Handle image updates
    let primaryImage = '';
    let additionalImages = [...productImages];
    
    // If we have images, the first one is the primary
    if (productImages.length > 0) {
      primaryImage = productImages[0];
      additionalImages = productImages.slice(1); // All images except the first
    }
    
    // If a new file was selected, add it to images
    if (selectedFile) {
      const newImageData = await convertFileToBase64(selectedFile);
      if (!primaryImage) {
        primaryImage = newImageData;
      } else {
        additionalImages.push(newImageData);
      }
    }
    
    // Calculate displayOrder for new products
    let displayOrder: number | undefined;
    if (isNewProduct) {
      // Find the highest displayOrder value and add 1
      const maxDisplayOrder = products.reduce((max, p) => {
        return Math.max(max, p.displayOrder || 0);
      }, 0);
      displayOrder = maxDisplayOrder + 1;
    } else {
      // Keep existing displayOrder for updates
      displayOrder = product?.displayOrder;
    }
    
    const updatedProduct: Product = {
      id: product?.id || `product-${Date.now()}`,
      name: formData.get('name') as string,
      category: formData.get('category') as string,
      basePrice: parseFloat(formData.get('basePrice') as string),
      description: formData.get('description') as string,
      image: primaryImage,
      images: additionalImages,
      customizable: isCustomizable,
      eligibleForVolumeDiscount: formData.get('eligibleForVolumeDiscount') !== null,
      minQuantity: parseInt(formData.get('minQuantity') as string) || 1,
      defaultWidth: formData.get('defaultWidth') ? parseInt(formData.get('defaultWidth') as string) : undefined,
      defaultHeight: formData.get('defaultHeight') ? parseInt(formData.get('defaultHeight') as string) : undefined,
      minWidth: formData.get('minWidth') ? parseInt(formData.get('minWidth') as string) : undefined,
      minHeight: formData.get('minHeight') ? parseInt(formData.get('minHeight') as string) : undefined,
      maxWidth: formData.get('maxWidth') ? parseInt(formData.get('maxWidth') as string) : undefined,
      maxHeight: formData.get('maxHeight') ? parseInt(formData.get('maxHeight') as string) : undefined,
      materials: materials,
      finishingOptions: finishingOptions,
      colorSpecs: colorSpecs,
      fixedSizes: fixedSizes,
      customOptions: customOptions,
      displayOrder: displayOrder,
      active: product?.active !== false, // Preserve active status
    };

    if (isNewProduct) {
      await addProduct(updatedProduct);
    } else {
      await updateProduct(updatedProduct);
    }

    navigate('/admin/products');
  };

  const handleToggleActive = async () => {
    if (product) {
      const newActiveStatus = product.active === false ? true : false;
      const action = newActiveStatus ? 'activate' : 'deactivate';
      if (window.confirm(`Are you sure you want to ${action} this product?`)) {
        await updateProduct({ ...product, active: newActiveStatus });
        navigate('/admin/products');
      }
    }
  };

  const addMaterial = () => {
    if (newMaterial.name && 
        ((newMaterial.pricingType === 'multiplier' && newMaterial.priceMultiplier && newMaterial.priceMultiplier > 0) ||
         (newMaterial.pricingType === 'fixed' && newMaterial.fixedCost !== undefined && newMaterial.fixedCost >= 0))) {
      const material = {
        ...newMaterial,
        id: `material-${Date.now()}`,
      };
      setMaterials(prev => [...prev, material]);
      setNewMaterial({ id: '', name: '', pricingType: 'multiplier', priceMultiplier: 1 });
    }
  };

  const removeMaterial = (id: string) => {
    setMaterials(prev => prev.filter(m => m.id !== id));
  };

  const updateMaterial = (id: string, updates: Partial<Material>) => {
    setMaterials(prev => prev.map(m => m.id === id ? { ...m, ...updates } : m));
  };

  const addFinishingOption = () => {
    if (newFinishing.name) {
      const finishing = {
        ...newFinishing,
        id: `finishing-${Date.now()}`,
      };
      setFinishingOptions(prev => [...prev, finishing]);
      setNewFinishing({ id: '', name: '', pricingType: 'flat', price: 0 });
    }
  };

  const removeFinishingOption = (id: string) => {
    setFinishingOptions(prev => prev.filter(f => f.id !== id));
  };

  const updateFinishingOption = (id: string, updates: Partial<FinishingOption>) => {
    setFinishingOptions(prev => prev.map(f => f.id === id ? { ...f, ...updates } : f));
  };

  const handleFinishingDragStart = (e: React.DragEvent, index: number) => {
    setDraggedFinishingIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleFinishingDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleFinishingDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    
    if (draggedFinishingIndex === null || draggedFinishingIndex === dropIndex) {
      return;
    }

    const newOptions = [...finishingOptions];
    const draggedOption = newOptions[draggedFinishingIndex];
    
    // Remove the dragged item
    newOptions.splice(draggedFinishingIndex, 1);
    
    // Insert at new position
    if (draggedFinishingIndex < dropIndex) {
      newOptions.splice(dropIndex - 1, 0, draggedOption);
    } else {
      newOptions.splice(dropIndex, 0, draggedOption);
    }
    
    setFinishingOptions(newOptions);
    setDraggedFinishingIndex(null);
  };

  const handleFinishingDragEnd = () => {
    setDraggedFinishingIndex(null);
  };

  const addColorSpec = () => {
    if (newColorSpec.name) {
      const colorSpec = {
        ...newColorSpec,
        id: `colorspec-${Date.now()}`,
      };
      setColorSpecs(prev => [...prev, colorSpec]);
      setNewColorSpec({ id: '', name: '', price: 0 });
    }
  };

  const removeColorSpec = (id: string) => {
    setColorSpecs(prev => prev.filter(c => c.id !== id));
  };

  const updateColorSpec = (id: string, updates: Partial<ColorSpec>) => {
    setColorSpecs(prev => prev.map(c => c.id === id ? { ...c, ...updates } : c));
  };

  const addFixedSize = () => {
    if (newFixedSize.width > 0 && newFixedSize.height > 0 && newFixedSize.price >= 0) {
      const fixedSize: FixedSize = {
        ...newFixedSize,
        id: `size-${Date.now()}`,
      };
      setFixedSizes(prev => [...prev, fixedSize]);
      setNewFixedSize({ id: '', width: 12, height: 12, price: 0, note: '' });
    }
  };

  const removeFixedSize = (id: string) => {
    setFixedSizes(prev => prev.filter(s => s.id !== id));
  };

  const updateFixedSize = (id: string, updates: Partial<FixedSize>) => {
    setFixedSizes(prev => prev.map(s => s.id === id ? { ...s, ...updates } : s));
  };

  const addCustomOption = () => {
    if (newCustomOption.name.trim()) {
      const customOption: CustomOption = {
        ...newCustomOption,
        id: `custom-option-${Date.now()}`,
        name: newCustomOption.name.trim(),
      };
      setCustomOptions(prev => [...prev, customOption]);
      setNewCustomOption({ id: '', name: '', required: false, options: [] });
    }
  };

  const removeCustomOption = (id: string) => {
    setCustomOptions(prev => prev.filter(opt => opt.id !== id));
  };

  const updateCustomOption = (id: string, updates: Partial<CustomOption>) => {
    setCustomOptions(prev => prev.map(opt => opt.id === id ? { ...opt, ...updates } : opt));
  };

  const addCustomOptionChoice = (optionId: string, choice: CustomOptionChoice) => {
    setCustomOptions(prev => prev.map(opt => {
      if (opt.id === optionId) {
        return { ...opt, options: [...opt.options, choice] };
      }
      return opt;
    }));
  };

  const removeCustomOptionChoice = (optionId: string, choiceId: string) => {
    setCustomOptions(prev => prev.map(opt => {
      if (opt.id === optionId) {
        return { ...opt, options: opt.options.filter(c => c.id !== choiceId) };
      }
      return opt;
    }));
  };

  const updateCustomOptionChoice = (optionId: string, choiceId: string, updates: Partial<CustomOptionChoice>) => {
    setCustomOptions(prev => prev.map(opt => {
      if (opt.id === optionId) {
        return {
          ...opt,
          options: opt.options.map(c => c.id === choiceId ? { ...c, ...updates } : c)
        };
      }
      return opt;
    }));
  };

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      
      // Validate file size (max 1MB to ensure compression works well)
      if (file.size > 1 * 1024 * 1024) {
        alert('File size must be less than 1MB');
        return;
      }
      
      setSelectedFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = (index: number) => {
    const newImages = [...productImages];
    newImages.splice(index, 1);
    setProductImages(newImages);
  };

  const addImageToProduct = async () => {
    if (selectedFile) {
      try {
        
        // Compress the image
        const compressedImage = await compressImage(selectedFile);
        const size = getBase64Size(compressedImage);
        
        // Check if compressed size is still too large (limit to ~100KB per image)
        if (size > 100 * 1024) {
          alert(`Image is too large even after compression (${formatBytes(size)}). Please use a smaller image.`);
          return;
        }
        
        setProductImages([...productImages, compressedImage]);
        setSelectedFile(null);
        setImagePreview('');
      } catch (error) {
        console.error('Failed to compress image:', error);
        alert('Failed to process image. Please try again.');
      }
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading product...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <p className="text-gray-600">Product not found</p>
          <Link to="/admin/products" className="text-blue-600 hover:underline mt-4 inline-block">
            Back to Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <Link to="/admin/products" className="inline-flex items-center text-blue-600 hover:underline mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Products
        </Link>
        <div className="flex justify-between items-center">
          <h1 className="text-4xl font-bold">
            {isNewProduct ? 'Add New Product' : 'Edit Product'}
          </h1>
          {!isNewProduct && (
            <button
              onClick={handleToggleActive}
              className={`${
                product?.active !== false
                  ? 'bg-orange-600 hover:bg-orange-700'
                  : 'bg-green-600 hover:bg-green-700'
              } text-white px-4 py-2 rounded-lg transition-colors flex items-center`}
            >
              {product?.active !== false ? (
                <>
                  <X className="h-5 w-5 mr-2" />
                  Deactivate Product
                </>
              ) : (
                <>
                  <Check className="h-5 w-5 mr-2" />
                  Activate Product
                </>
              )}
            </button>
          )}
        </div>
        {error && (
          <div className="mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Product Name *
              </label>
              <input
                type="text"
                name="name"
                defaultValue={product?.name}
                required
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category *
              </label>
              <select
                name="category"
                defaultValue={product?.category}
                required
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a category</option>
                {categories
                  .filter(cat => cat.active !== false)
                  .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
                  .map(category => (
                    <option key={category.id} value={category.name}>
                      {category.name}
                    </option>
                  ))}
              </select>
            </div>
            {isCustomizable ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Base Price per sq ft *
                </label>
                <input
                  type="number"
                  name="basePrice"
                  defaultValue={product?.basePrice}
                  step="0.001"
                  min="0"
                  required
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Base Price
                </label>
                <input
                  type="hidden"
                  name="basePrice"
                  value={product?.basePrice || 0}
                />
                <p className="px-4 py-2 bg-gray-100 rounded-lg text-gray-600">
                  Prices are set per size option below
                </p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minimum Order Quantity *
              </label>
              <input
                type="number"
                name="minQuantity"
                defaultValue={product?.minQuantity || 1}
                min="1"
                required
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Product Images
              </label>
              <div className="space-y-4">
                {/* Display existing images */}
                {productImages.length > 0 && (
                  <div className="grid grid-cols-4 gap-4">
                    {productImages.map((img, index) => (
                      <div key={index} className="relative group">
                        <img 
                          src={img} 
                          alt={`Product ${index + 1}`} 
                          className="w-full h-32 object-cover rounded-lg border"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity rounded-lg flex items-center justify-center">
                          {index === 0 && (
                            <span className="absolute top-2 left-2 bg-blue-600 text-white text-xs px-2 py-1 rounded">Primary</span>
                          )}
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="opacity-0 group-hover:opacity-100 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-opacity"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Image upload section */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                  {imagePreview ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-center">
                        <img 
                          src={imagePreview} 
                          alt="Preview" 
                          className="max-h-48 rounded-lg"
                        />
                      </div>
                      <div className="flex justify-center gap-4">
                        <button
                          type="button"
                          onClick={addImageToProduct}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                        >
                          Add to Product
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedFile(null);
                            setImagePreview('');
                          }}
                          className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center">
                      <label className="inline-flex items-center px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg cursor-pointer hover:bg-blue-100">
                        <Upload className="h-4 w-4 mr-2 text-blue-600" />
                        <span className="text-blue-600">Choose Image</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange}
                          className="hidden"
                        />
                      </label>
                      <p className="mt-2 text-xs text-gray-500">
                        Accepted formats: JPG, PNG, GIF. Max size: 1MB. First image will be the primary image.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              name="description"
              defaultValue={product?.description}
              rows={3}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="customizable"
                value="true"
                checked={isCustomizable}
                onChange={(e) => setIsCustomizable(e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm font-medium text-gray-700">Customizable Size</span>
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                name="eligibleForVolumeDiscount"
                value="true"
                defaultChecked={product?.eligibleForVolumeDiscount !== false}
                className="mr-2"
              />
              <span className="text-sm font-medium text-gray-700">Eligible for Volume Discounts</span>
            </label>
          </div>

          {isCustomizable && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Default Size for Instant Quote</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Default Width (inches)
                    </label>
                    <input
                      type="number"
                      name="defaultWidth"
                      defaultValue={product?.defaultWidth}
                      min="1"
                      placeholder="Initial width in quote form"
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Default Height (inches)
                    </label>
                    <input
                      type="number"
                      name="defaultHeight"
                      defaultValue={product?.defaultHeight}
                      min="1"
                      placeholder="Initial height in quote form"
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Size Limits</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Min Width (inches)
                    </label>
                    <input
                      type="number"
                      name="minWidth"
                      defaultValue={product?.minWidth}
                      min="1"
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Min Height (inches)
                    </label>
                    <input
                      type="number"
                      name="minHeight"
                      defaultValue={product?.minHeight}
                      min="1"
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Max Width (inches)
                    </label>
                    <input
                      type="number"
                      name="maxWidth"
                      defaultValue={product?.maxWidth}
                      min="1"
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Max Height (inches)
                    </label>
                    <input
                      type="number"
                      name="maxHeight"
                      defaultValue={product?.maxHeight}
                      min="1"
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {!isCustomizable && (
              <div className="mt-4">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Fixed Size Options</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Since this product is not customizable, define the available sizes and their prices.
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="space-y-2 mb-4">
                    {fixedSizes.map((size) => (
                      <div key={size.id} className="p-3 bg-white border rounded-lg">
                        {editingFixedSizeId === size.id ? (
                          <div className="space-y-2">
                            <div className="grid grid-cols-4 gap-2">
                              <input
                                type="number"
                                value={size.width}
                                onChange={(e) => updateFixedSize(size.id, { width: parseFloat(e.target.value) || 0 })}
                                className="px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-blue-500"
                                placeholder="Width"
                                step="0.01"
                                min="1"
                              />
                              <input
                                type="number"
                                value={size.height}
                                onChange={(e) => updateFixedSize(size.id, { height: parseFloat(e.target.value) || 0 })}
                                className="px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-blue-500"
                                placeholder="Height"
                                step="0.01"
                                min="1"
                              />
                              <input
                                type="number"
                                value={size.price}
                                onChange={(e) => updateFixedSize(size.id, { price: parseFloat(e.target.value) || 0 })}
                                className="px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-blue-500"
                                placeholder="Price"
                                min="0"
                                step="0.01"
                              />
                              <div className="flex gap-1">
                                <button
                                  type="button"
                                  onClick={() => setEditingFixedSizeId(null)}
                                  className="p-1 text-green-600 hover:bg-green-50 rounded"
                                >
                                  <Check className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => removeFixedSize(size.id)}
                                  className="p-1 text-red-600 hover:bg-red-50 rounded"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                            <input
                              type="text"
                              value={size.note || ''}
                              onChange={(e) => updateFixedSize(size.id, { note: e.target.value })}
                              className="w-full px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-blue-500"
                              placeholder="Optional note (e.g., 'Door size', 'Most popular')"
                            />
                          </div>
                        ) : (
                          <div className="flex justify-between items-center">
                            <span className="text-sm">
                              {size.width}" × {size.height}" - ${size.price.toFixed(2)}
                              {size.note && <span className="text-gray-500 ml-2">({size.note})</span>}
                            </span>
                            <button
                              type="button"
                              onClick={() => setEditingFixedSizeId(size.id)}
                              className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="space-y-2">
                    <div className="grid grid-cols-4 gap-2">
                      <input
                        type="number"
                        value={newFixedSize.width}
                        onChange={(e) => setNewFixedSize({ ...newFixedSize, width: parseFloat(e.target.value) || 0 })}
                        placeholder="Width (inches)"
                        className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        min="0.01"
                        step="0.01"
                      />
                      <input
                        type="number"
                        value={newFixedSize.height}
                        onChange={(e) => setNewFixedSize({ ...newFixedSize, height: parseFloat(e.target.value) || 0 })}
                        placeholder="Height (inches)"
                        className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        min="0.01"
                        step="0.01"
                      />
                      <input
                        type="number"
                        value={newFixedSize.price}
                        onChange={(e) => setNewFixedSize({ ...newFixedSize, price: parseFloat(e.target.value) || 0 })}
                        placeholder="Price ($)"
                        className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        min="0"
                        step="0.01"
                      />
                      <button
                        type="button"
                        onClick={addFixedSize}
                        className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center text-sm"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Size
                      </button>
                    </div>
                    <input
                      type="text"
                      value={newFixedSize.note || ''}
                      onChange={(e) => setNewFixedSize({ ...newFixedSize, note: e.target.value })}
                      placeholder="Optional note (e.g., 'Door size', 'Most popular')"
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            )}

          <div className="border-t pt-6 mt-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Product Options</h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-2">Materials</h3>
              <p className="text-sm text-gray-600 mb-4">
                Add materials specific to this product. Choose between percentage multiplier or fixed cost per square foot.
              </p>
              <div className="space-y-2 mb-4">
                {materials.map((material) => (
                  <div key={material.id} className="p-2 bg-white border rounded">
                    {editingMaterialId === material.id ? (
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={material.name}
                          onChange={(e) => updateMaterial(material.id, { name: e.target.value })}
                          className="w-full px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="flex gap-2">
                          <select
                            value={material.pricingType}
                            onChange={(e) => updateMaterial(material.id, { 
                              pricingType: e.target.value as 'multiplier' | 'fixed',
                              priceMultiplier: e.target.value === 'multiplier' ? (material.priceMultiplier || 1) : undefined,
                              fixedCost: e.target.value === 'fixed' ? (material.fixedCost || 0) : undefined
                            })}
                            className="px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="multiplier">Multiplier</option>
                            <option value="fixed">Fixed Cost</option>
                          </select>
                          {material.pricingType === 'multiplier' ? (
                            <input
                              type="number"
                              value={material.priceMultiplier || ''}
                              onChange={(e) => updateMaterial(material.id, { priceMultiplier: parseFloat(e.target.value) || 1 })}
                              step="0.001"
                              min="0.001"
                              className="flex-1 px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-blue-500"
                            />
                          ) : (
                            <input
                              type="number"
                              value={material.fixedCost || ''}
                              onChange={(e) => updateMaterial(material.id, { fixedCost: parseFloat(e.target.value) || 0 })}
                              step="0.001"
                              min="0"
                              className="flex-1 px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-blue-500"
                            />
                          )}
                          <button
                            type="button"
                            onClick={() => setEditingMaterialId(null)}
                            className="text-green-600 hover:text-green-800"
                          >
                            <Check className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-medium">{material.name}</span>
                          <span className="ml-2 text-sm text-gray-600">
                            {material.pricingType === 'multiplier' ? (
                              <>
                                (×{material.priceMultiplier}
                                {material.priceMultiplier !== 1 && (
                                  <span className={material.priceMultiplier! > 1 ? 'text-red-600' : 'text-green-600'}>
                                    {material.priceMultiplier! > 1 ? ' +' : ' '}
                                    {((material.priceMultiplier! - 1) * 100).toFixed(0)}%
                                  </span>
                                )}
                                )
                              </>
                            ) : (
                              <span className={material.fixedCost! > 0 ? 'text-red-600' : 'text-gray-600'}>
                                (+${material.fixedCost}/sq ft)
                              </span>
                            )}
                          </span>
                        </div>
                        <div className="flex gap-1">
                          <button
                            type="button"
                            onClick={() => setEditingMaterialId(material.id)}
                            className="text-blue-600 hover:text-blue-800 p-1"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => removeMaterial(material.id)}
                            className="text-red-600 hover:text-red-800 p-1"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Material name"
                    value={newMaterial.name}
                    onChange={(e) => setNewMaterial(prev => ({ ...prev, name: e.target.value }))}
                    className="flex-1 px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <select
                    value={newMaterial.pricingType}
                    onChange={(e) => setNewMaterial(prev => ({ 
                      ...prev, 
                      pricingType: e.target.value as 'multiplier' | 'fixed',
                      priceMultiplier: e.target.value === 'multiplier' ? 1 : undefined,
                      fixedCost: e.target.value === 'fixed' ? 0 : undefined
                    }))}
                    className="px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="multiplier">Multiplier</option>
                    <option value="fixed">Fixed Cost</option>
                  </select>
                </div>
                <div className="flex gap-2">
                  {newMaterial.pricingType === 'multiplier' ? (
                    <input
                      type="number"
                      placeholder="1.0"
                      value={newMaterial.priceMultiplier || ''}
                      onChange={(e) => setNewMaterial(prev => ({ ...prev, priceMultiplier: parseFloat(e.target.value) || 1 }))}
                      step="0.001"
                      min="0.001"
                      className="flex-1 px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <input
                      type="number"
                      placeholder="0.00"
                      value={newMaterial.fixedCost || ''}
                      onChange={(e) => setNewMaterial(prev => ({ ...prev, fixedCost: parseFloat(e.target.value) || 0 }))}
                      step="0.001"
                      min="0"
                      className="flex-1 px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500"
                    />
                  )}
                  <button
                    type="button"
                    onClick={addMaterial}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Add
                  </button>
                </div>
                <p className="text-xs text-gray-500">
                  {newMaterial.pricingType === 'multiplier' 
                    ? 'Multiplier: 1.0 = base price, 1.25 = 25% increase, 0.85 = 15% decrease'
                    : 'Fixed cost: additional cost per square foot (e.g., $2.50/sq ft)'
                  }
                </p>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold mb-2">Finishing Options</h3>
              <p className="text-sm text-gray-600 mb-4">
                Add finishing options specific to this product. Choose between flat fee or cost per square foot.
              </p>
              <div className="space-y-2 mb-4">
                {finishingOptions.map((option, index) => (
                  <div 
                    key={option.id} 
                    className={`p-2 bg-white border rounded cursor-move hover:shadow-md transition-shadow ${draggedFinishingIndex === index ? 'opacity-50' : ''}`}
                    draggable={editingFinishingId !== option.id}
                    onDragStart={(e) => handleFinishingDragStart(e, index)}
                    onDragOver={handleFinishingDragOver}
                    onDrop={(e) => handleFinishingDrop(e, index)}
                    onDragEnd={handleFinishingDragEnd}
                  >
                    {editingFinishingId === option.id ? (
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={option.name}
                          onChange={(e) => updateFinishingOption(option.id, { name: e.target.value })}
                          className="w-full px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="flex gap-2">
                          <select
                            value={option.pricingType}
                            onChange={(e) => updateFinishingOption(option.id, { 
                              pricingType: e.target.value as 'flat' | 'perSqFt'
                            })}
                            className="px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="flat">Flat Fee</option>
                            <option value="perSqFt">Per Sq Ft</option>
                          </select>
                          <input
                            type="number"
                            value={option.price}
                            onChange={(e) => updateFinishingOption(option.id, { price: parseFloat(e.target.value) || 0 })}
                            step="0.001"
                            min="0"
                            placeholder={option.pricingType === 'flat' ? "0.00" : "0.00/sq ft"}
                            className="flex-1 px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-blue-500"
                          />
                          <button
                            type="button"
                            onClick={() => setEditingFinishingId(null)}
                            className="text-green-600 hover:text-green-800"
                          >
                            <Check className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <GripVertical className="h-4 w-4 text-gray-400 cursor-move" />
                          <div>
                            <span className="font-medium">{option.name}</span>
                            <span className="ml-2 text-sm text-gray-600">
                              {option.price === 0 ? '(Free)' : 
                                option.pricingType === 'flat' ? `(+$${option.price})` : `(+$${option.price}/sq ft)`}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <button
                            type="button"
                            onClick={() => setEditingFinishingId(option.id)}
                            className="text-blue-600 hover:text-blue-800 p-1"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => removeFinishingOption(option.id)}
                            className="text-red-600 hover:text-red-800 p-1"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Finishing option name"
                    value={newFinishing.name}
                    onChange={(e) => setNewFinishing(prev => ({ ...prev, name: e.target.value }))}
                    className="flex-1 px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <select
                    value={newFinishing.pricingType}
                    onChange={(e) => setNewFinishing(prev => ({ 
                      ...prev, 
                      pricingType: e.target.value as 'flat' | 'perSqFt'
                    }))}
                    className="px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="flat">Flat Fee</option>
                    <option value="perSqFt">Per Sq Ft</option>
                  </select>
                </div>
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder={newFinishing.pricingType === 'flat' ? "0.00" : "0.00 per sq ft"}
                    value={newFinishing.price}
                    onChange={(e) => setNewFinishing(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                    step="0.001"
                    min="0"
                    className="flex-1 px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={addFinishingOption}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Add
                  </button>
                </div>
                <p className="text-xs text-gray-500">
                  {newFinishing.pricingType === 'flat' 
                    ? 'Flat fee: one-time additional cost regardless of size'
                    : 'Per sq ft: additional cost multiplied by the sign area'
                  }
                </p>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold mb-2">Color Specs</h3>
              <p className="text-sm text-gray-600 mb-4">
                Add color specifications specific to this product. Price is additional fixed cost per square foot.
              </p>
              <div className="space-y-2 mb-4">
                {colorSpecs.map((colorSpec) => (
                  <div key={colorSpec.id} className="p-2 bg-white border rounded">
                    {editingColorSpecId === colorSpec.id ? (
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={colorSpec.name}
                          onChange={(e) => updateColorSpec(colorSpec.id, { name: e.target.value })}
                          className="w-full px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="flex gap-2">
                          <input
                            type="number"
                            value={colorSpec.price}
                            onChange={(e) => updateColorSpec(colorSpec.id, { price: parseFloat(e.target.value) || 0 })}
                            step="0.001"
                            min="0"
                            placeholder="0.00 per sq ft"
                            className="flex-1 px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-blue-500"
                          />
                          <button
                            type="button"
                            onClick={() => setEditingColorSpecId(null)}
                            className="text-green-600 hover:text-green-800"
                          >
                            <Check className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-medium">{colorSpec.name}</span>
                          <span className="ml-2 text-sm text-gray-600">
                            {colorSpec.price === 0 ? '(Free)' : `(+$${colorSpec.price}/sq ft)`}
                          </span>
                        </div>
                        <div className="flex gap-1">
                          <button
                            type="button"
                            onClick={() => setEditingColorSpecId(colorSpec.id)}
                            className="text-blue-600 hover:text-blue-800 p-1"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => removeColorSpec(colorSpec.id)}
                            className="text-red-600 hover:text-red-800 p-1"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Color spec name"
                  value={newColorSpec.name}
                  onChange={(e) => setNewColorSpec(prev => ({ ...prev, name: e.target.value }))}
                  className="flex-1 px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="number"
                  placeholder="0.00"
                  value={newColorSpec.price}
                  onChange={(e) => setNewColorSpec(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                  step="0.001"
                  min="0"
                  className="w-20 px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={addColorSpec}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Add
                </button>
              </div>
            </div>
          </div>
          </div>

          {/* Custom Options Section */}
          <div className="border-t pt-6 mt-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Custom Product Options</h2>
            <p className="text-gray-600 mb-4">
              Add custom option categories beyond the standard materials, finishes, and colors. Examples: Frame Style, Mounting Type, etc.
            </p>
            
            <div className="space-y-6">
              {customOptions.map((option) => (
                <div key={option.id} className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-start justify-between mb-4">
                    {editingCustomOptionId === option.id ? (
                      <div className="flex-1 mr-4 space-y-2">
                        <input
                          type="text"
                          value={option.name}
                          onChange={(e) => updateCustomOption(option.id, { name: e.target.value })}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="Option name"
                        />
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={option.required}
                            onChange={(e) => updateCustomOption(option.id, { required: e.target.checked })}
                            className="mr-2"
                          />
                          <span className="text-sm">Required option</span>
                        </label>
                      </div>
                    ) : (
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold">{option.name}</h3>
                        <p className="text-sm text-gray-600">
                          {option.required ? 'Required' : 'Optional'} • {option.options.length} choices
                        </p>
                      </div>
                    )}
                    <div className="flex gap-2">
                      {editingCustomOptionId === option.id ? (
                        <button
                          type="button"
                          onClick={() => setEditingCustomOptionId(null)}
                          className="text-green-600 hover:text-green-800 p-1"
                        >
                          <Check className="h-5 w-5" />
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setEditingCustomOptionId(option.id)}
                          className="text-blue-600 hover:text-blue-800 p-1"
                        >
                          <Edit2 className="h-5 w-5" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => removeCustomOption(option.id)}
                        className="text-red-600 hover:text-red-800 p-1"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>

                  {/* Option Choices */}
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-gray-700">Choices:</h4>
                    {option.options.map((choice) => (
                      <div key={choice.id} className="p-2 bg-white border rounded">
                        <div className="flex items-center justify-between">
                          <input
                            type="text"
                            value={choice.name}
                            onChange={(e) => updateCustomOptionChoice(option.id, choice.id, { name: e.target.value })}
                            className="flex-1 px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-blue-500 mr-2"
                          />
                          <select
                            value={choice.pricingType}
                            onChange={(e) => {
                              const pricingType = e.target.value as 'multiplier' | 'fixed' | 'flat';
                              updateCustomOptionChoice(option.id, choice.id, {
                                pricingType,
                                priceMultiplier: pricingType === 'multiplier' ? 1 : undefined,
                                fixedCost: pricingType === 'fixed' ? 0 : undefined,
                                flatCost: pricingType === 'flat' ? 0 : undefined
                              });
                            }}
                            className="px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-blue-500 mr-2"
                          >
                            <option value="flat">Flat</option>
                            <option value="multiplier">×</option>
                            <option value="fixed">Per ft²</option>
                          </select>
                          {choice.pricingType === 'multiplier' && (
                            <input
                              type="number"
                              value={choice.priceMultiplier || 1}
                              onChange={(e) => updateCustomOptionChoice(option.id, choice.id, { priceMultiplier: parseFloat(e.target.value) || 1 })}
                              step="0.1"
                              min="0"
                              className="w-20 px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-blue-500 mr-2"
                            />
                          )}
                          {choice.pricingType === 'fixed' && (
                            <input
                              type="number"
                              value={choice.fixedCost || 0}
                              onChange={(e) => updateCustomOptionChoice(option.id, choice.id, { fixedCost: parseFloat(e.target.value) || 0 })}
                              step="0.01"
                              min="0"
                              placeholder="$/ft²"
                              className="w-20 px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-blue-500 mr-2"
                            />
                          )}
                          {choice.pricingType === 'flat' && (
                            <input
                              type="number"
                              value={choice.flatCost || 0}
                              onChange={(e) => updateCustomOptionChoice(option.id, choice.id, { flatCost: parseFloat(e.target.value) || 0 })}
                              step="0.01"
                              min="0"
                              placeholder="$"
                              className="w-20 px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-blue-500 mr-2"
                            />
                          )}
                          <button
                            type="button"
                            onClick={() => removeCustomOptionChoice(option.id, choice.id)}
                            className="text-red-600 hover:text-red-800 p-1"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                    
                    {/* Add new choice */}
                    <div className="flex gap-2 mt-3">
                      <input
                        type="text"
                        placeholder="Choice name"
                        className="flex-1 px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const input = e.target as HTMLInputElement;
                            if (input.value.trim()) {
                              addCustomOptionChoice(option.id, {
                                id: `choice-${Date.now()}`,
                                name: input.value.trim(),
                                pricingType: 'flat',
                                flatCost: 0
                              });
                              input.value = '';
                            }
                          }
                        }}
                      />
                      <select
                        className="px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500"
                        onChange={(e) => {
                          const input = e.target.previousElementSibling as HTMLInputElement;
                          if (input.value.trim()) {
                            const pricingType = e.target.value as 'multiplier' | 'fixed' | 'flat';
                            addCustomOptionChoice(option.id, {
                              id: `choice-${Date.now()}`,
                              name: input.value.trim(),
                              pricingType,
                              priceMultiplier: pricingType === 'multiplier' ? 1 : undefined,
                              fixedCost: pricingType === 'fixed' ? 0 : undefined,
                              flatCost: pricingType === 'flat' ? 0 : undefined
                            });
                            input.value = '';
                          }
                        }}
                      >
                        <option value="flat">Flat fee</option>
                        <option value="multiplier">Multiplier</option>
                        <option value="fixed">Per sq ft</option>
                      </select>
                    </div>
                  </div>
                </div>
              ))}

              {/* Add new custom option */}
              <div className="bg-white p-4 rounded-lg border-2 border-dashed border-gray-300">
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="New option category name (e.g., Frame Style)"
                    value={newCustomOption.name}
                    onChange={(e) => setNewCustomOption({ ...newCustomOption, name: e.target.value })}
                    className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={addCustomOption}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                  >
                    <Plus className="h-5 w-5 mr-2" />
                    Add Option
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-between pt-6 border-t">
            <Link
              to="/admin/products"
              className="bg-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-400 transition-colors"
            >
              Cancel
            </Link>
            <button
              type="submit"
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
            >
              <Save className="h-5 w-5 mr-2" />
              {isNewProduct ? 'Create Product' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminProductDetail;